import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';

function escapeHtml(s: string) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function safeExec(command: string, value?: string) {
  try {
    document.execCommand(command, false, value);
  } catch {
    // ignore
  }
}

export type RichTextEditorHandle = {
  focus: () => void;
  getHtml: () => string;
  setHtml: (html: string) => void;
  insertText: (text: string) => void;
  insertHtml: (html: string) => void;
};

type Props = {
  valueHtml: string;
  onChangeHtml: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  disabled?: boolean;
};

export const RichTextEditor = forwardRef<RichTextEditorHandle, Props>(function RichTextEditor(
  { valueHtml, onChangeHtml, placeholder = 'Write your email…', minHeight = 240, disabled = false },
  ref
) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const lastHtmlRef = useRef<string>(valueHtml || '');

  const emitChange = () => {
    const el = editorRef.current;
    if (!el) return;
    const next = el.innerHTML || '';
    lastHtmlRef.current = next;
    onChangeHtml(next);
  };

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const next = valueHtml || '';
    if (next === lastHtmlRef.current) return;
    el.innerHTML = next;
    lastHtmlRef.current = next;
  }, [valueHtml]);

  const api = useMemo<RichTextEditorHandle>(
    () => ({
      focus: () => editorRef.current?.focus(),
      getHtml: () => editorRef.current?.innerHTML || '',
      setHtml: (html: string) => {
        const el = editorRef.current;
        if (!el) return;
        el.innerHTML = html || '';
        lastHtmlRef.current = el.innerHTML || '';
        onChangeHtml(lastHtmlRef.current);
      },
      insertText: (text: string) => {
        editorRef.current?.focus();
        if (disabled) return;
        safeExec('insertText', text);
        // Fallback for browsers without insertText support.
        if ((editorRef.current?.innerHTML || '') === lastHtmlRef.current) safeExec('insertHTML', escapeHtml(text));
        emitChange();
      },
      insertHtml: (html: string) => {
        editorRef.current?.focus();
        if (disabled) return;
        safeExec('insertHTML', html);
        emitChange();
      },
    }),
    [disabled, onChangeHtml]
  );

  useImperativeHandle(ref, () => api, [api]);

  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    // Keep it clean: paste as plain text (avoid weird email-client HTML).
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    safeExec('insertText', text);
    emitChange();
  };

  const cmdBtn = (label: string, command: string, value?: string) => (
    <button
      type="button"
      className="rte__btn"
      onClick={() => {
        if (disabled) return;
        editorRef.current?.focus();
        safeExec(command, value);
        emitChange();
      }}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      {label}
    </button>
  );

  const onLink = () => {
    if (disabled) return;
    const url = window.prompt('Link URL (https://…)');
    if (!url) return;
    editorRef.current?.focus();
    safeExec('createLink', url);
    emitChange();
  };

  const onClear = () => {
    if (disabled) return;
    if (!window.confirm('Clear email body?')) return;
    api.setHtml('');
  };

  return (
    <div className={`rte ${disabled ? 'is-disabled' : ''}`}>
      <div className="rte__toolbar" role="toolbar" aria-label="Formatting">
        {cmdBtn('B', 'bold')}
        {cmdBtn('I', 'italic')}
        {cmdBtn('U', 'underline')}
        <span className="rte__sep" aria-hidden="true" />
        {cmdBtn('• List', 'insertUnorderedList')}
        {cmdBtn('1. List', 'insertOrderedList')}
        <span className="rte__sep" aria-hidden="true" />
        <button type="button" className="rte__btn" onClick={onLink} disabled={disabled} aria-label="Insert link" title="Insert link">
          Link
        </button>
        <span style={{ flex: 1 }} />
        <button type="button" className="rte__btn rte__btn--danger" onClick={onClear} disabled={disabled} aria-label="Clear">
          Clear
        </button>
      </div>
      <div
        ref={editorRef}
        className="rte__editor"
        contentEditable={!disabled}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={emitChange}
        onBlur={emitChange}
        onPaste={onPaste}
        style={{ minHeight }}
      />
    </div>
  );
});

